/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */

import React, { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';
import { di } from 'react-magnetic-di';
import { Link, useRouterActions } from 'react-resource-router';

// eslint-disable-next-line @atlaskit/design-system/no-deprecated-imports
import { Profile } from '@atlaskit/atlassian-navigation/profile';
import Avatar from '@atlaskit/avatar/avatar';
import Button from '@atlaskit/button/default/button';
import IconButton from '@atlaskit/button/icon/button';
import { cssMap, jsx, cx } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import HomeIcon from '@atlaskit/icon/core/home';
import InboxIcon from '@atlaskit/icon/core/inbox';
import MenuIcon from '@atlaskit/icon/core/menu';
import Motion from '@atlaskit/motion/entering/motion';
import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Popup } from '@atlaskit/popup/popup';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { UNSAFE_useMediaQuery } from '@atlaskit/primitives/compiled/use-media-query';
import { token } from '@atlaskit/tokens';
import type { IconColor } from '@atlaskit/tokens/css-type-schema';
import { useNavigationStyle } from '@atlassian/company-hub-common/controllers/navigation-style-context';
import { useHubAutoTextColor } from '@atlassian/company-hub-common/hub-auto-text-color';
import {
	HUB_CORNER_RADIUS_CSS_VAR,
	resolveHubCornerRadius,
} from '@atlassian/company-hub-common/hub-corner-radius';
import { useIsHubCornerRadiusEnabled } from '@atlassian/company-hub-common/use-is-hub-corner-radius-enabled';
import { useShouldShowPremiumUpsell } from '@atlassian/company-hub-common/use-should-show-premium-upsell';
import { useHubContext } from '@atlassian/company-hub-common/utils';
import { getHubLogoHeight } from '@atlassian/company-hub-common/utils/hub-branding';

import { AvatarMenu } from '../avatar-menu';
import messages from './messages';
import { NavLinks } from './nav-links';

const MobileMenuOverlay = lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_hub-mobile-menu-overlay" */ './mobile-menu-overlay'
	).then((m) => ({ default: m.MobileMenuOverlay })),
);

const motionStyles = cssMap({
	slideEntering: {
		animationName: `${token('motion.keyframe.slide.in.top.short')}, ${token(
			'motion.keyframe.fade.in',
		)}`,
		animationDuration: token('motion.duration.medium'),
		animationTimingFunction: token('motion.easing.out.practical'),
	},
	slideExiting: {
		animationName: `${token('motion.keyframe.slide.out.top.short')}, ${token(
			'motion.keyframe.fade.out',
		)}`,
		animationDuration: token('motion.duration.medium'),
		animationTimingFunction: token('motion.easing.in.practical'),
	},
});

const styles = cssMap({
	// Navbar menus render inline via `shouldRenderToParent`, so they inherit this
	// stacking context and need to out-rank hub content (which tops out at 100).
	navbarStacking: {
		position: 'relative',
		zIndex: 200,
	},
	navbar: {
		paddingTop: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
	},
	navbarUpdated: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
	},
	navBarDisabled: {
		pointerEvents: 'none',
	},
	logoContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	rightSection: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.150'),
		flexShrink: 0,
	},
});

export const Navbar = ({
	isEditMode = false,
	isViewportSticky = false,
}: {
	isEditMode?: boolean;
	/** Gated by HubAppShell, which owns viewport scrolling and the padded sticky wrapper. */
	isViewportSticky?: boolean;
}): JSX.Element => {
	di(fg, useHubContext, useRouterActions);
	const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
	const [viewportMatches, setViewportMatches] = useState<boolean>();
	const navbarRef = useRef<HTMLDivElement>(null);

	const isMobileViewEnabled = fg('hubs_nav_mobile_view');
	const isViewportSwitchEnabled = fg('switch-hub-nav-bar') && isMobileViewEnabled;
	const handleViewportChange = useCallback((event: MediaQueryListEvent) => {
		setViewportMatches(event.matches);
		setIsMobileMenuOpen(false);
		setIsAvatarMenuOpen(false);
	}, []);
	const mediaQuery = UNSAFE_useMediaQuery(
		'below.sm',
		isViewportSwitchEnabled ? handleViewportChange : undefined,
	);
	const isBelowSm =
		(isViewportSwitchEnabled ? viewportMatches : undefined) ?? mediaQuery?.matches ?? false;

	const hubContext = useHubContext();
	const { featureFlags, hubType } = hubContext;
	const { options: navStyle } = useNavigationStyle();

	// Keep a black/white navbar label legible against the live brand colour. The
	// navbar's day-0 background is the darkest brand shade, so a brand change can
	// invert which of black/white is readable. An explicit colour is left alone.
	// See `hub-auto-text-color`.
	const resolveNavTextColor = useHubAutoTextColor('hub-app-shell');
	const navTextColor = resolveNavTextColor({
		backgroundColor: navStyle?.backgroundColor,
		textColor: navStyle?.textColor,
		defaultRef: 'primary.1000',
	});

	const isNavBarUpdated = featureFlags?.hub_component_upgrade ?? false;
	const isLoginButtonContrastGateEnabled = fg('hubs_nav_login_button_contrast');
	const isLoginButtonContrastEnabled = isNavBarUpdated && isLoginButtonContrastGateEnabled;
	const isMenuStackingFixEnabled = fg('hubs_nav_menu_stacking_fix');
	const isCornerRadiusEnabled = useIsHubCornerRadiusEnabled();
	// A navbar-specific corner radius is Premium. Suppressing it makes the navbar
	// inherit the hub-wide radius, which the backend resets to the 8px default.
	// Only the gated path is neutralised: the legacy path below has no hub-wide
	// value to fall back to, so it keeps applying the saved radius.
	const isCornerRadiusLocked = useShouldShowPremiumUpsell();

	const shouldShowMobileNavLinks = isNavBarUpdated && isBelowSm && isMobileViewEnabled;

	const dynamicStyle = useMemo<React.CSSProperties | undefined>(() => {
		if (!navStyle) {
			return undefined;
		}
		const border = `1px solid ${navStyle.borderColor}`;

		// Gate ON: always drive from the hub-wide CSS var; pin it locally only when
		// this navbar has an explicit numeric radius, otherwise inherit the global.
		// Gate OFF: legacy fixed px (cornerRadius is always a number here).
		const cornerRadiusStyle: React.CSSProperties = isCornerRadiusEnabled
			? {
					borderRadius: navStyle.padding ? `var(${HUB_CORNER_RADIUS_CSS_VAR})` : '0',
					...(!isCornerRadiusLocked && navStyle.padding && typeof navStyle.cornerRadius === 'number'
						? {
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
								[HUB_CORNER_RADIUS_CSS_VAR]: `${resolveHubCornerRadius(navStyle.cornerRadius)}px`,
							}
						: {}),
				}
			: // Gate OFF: legacy fixed px. `cornerRadius` is typed `number | null`; if an
				// unset (null) value ever reaches this branch, fall back to the legacy
				// 8px default (matching the mapper's gate-off coercion) rather than
				// rendering an invalid "nullpx".
				{ borderRadius: navStyle.padding ? `${navStyle.cornerRadius ?? 8}px` : '0' };

		// Fixed ("Fixed to top") navbar layout differs by cohort.
		const fixedNavLayoutStyle: React.CSSProperties = isViewportSticky
			? {
					// The shell wraps padded fixed navbars in a sticky, background-covered
					// container in view mode. That wrapper owns the top gap so it cannot
					// scroll away; don't add a second offset or margin to the navbar.
					top: '0px',
				}
			: {
					// Legacy: sticky offset provides the top gap and a low z-index.
					top: navStyle.padding && navStyle.fixedAtTop ? token('space.300') : '0px',
					// Gate ON: z-index lives in `styles.navbarStacking`; an inline one would override it.
					zIndex: !isMenuStackingFixEnabled && navStyle.fixedAtTop ? 1 : undefined,
					marginTop: navStyle.padding ? token('space.300') : undefined,
				};

		return {
			backgroundColor: navStyle.backgroundColor,
			color: navTextColor,
			...cornerRadiusStyle,
			border: navStyle.borderType === 'ALL_BORDER' ? border : 'none', // this will be the css 'none' value
			borderBottom: ['BOTTOM_BORDER', 'ALL_BORDER'].includes(navStyle.borderType)
				? border
				: undefined, // bottom border is always applied for all borders
			position: navStyle.fixedAtTop ? 'sticky' : 'relative',
			...fixedNavLayoutStyle,
			marginLeft: navStyle.padding ? token('space.300') : undefined,
			marginRight: navStyle.padding ? token('space.300') : undefined,
			// The padded sticky wrapper owns the bottom gap in view mode, keeping
			// that spacing outside its background-covered area while scrolling.
			marginBottom: isViewportSticky && navStyle.padding ? 0 : token('space.300'),
			transition: 'margin 0.3s ease, border-radius 0.3s ease, top 0.3s ease',
		};
	}, [
		navStyle,
		navTextColor,
		isCornerRadiusEnabled,
		isCornerRadiusLocked,
		isMenuStackingFixEnabled,
		isViewportSticky,
	]);

	const iconColor = isNavBarUpdated ? (navTextColor as IconColor) : undefined;

	const { push } = useRouterActions();

	const { formatMessage } = useIntl();

	const logoUrl = hubContext.branding.logo.url;
	const { avatarSrc, email } = hubContext.user;
	const { renderCustomNavActions, renderRightNavActions } = hubContext;

	const isAnonymousRoute = hubContext.layout?.isAnonymousRoute || false;
	const homePath = isAnonymousRoute ? '/user/login' : '/';
	const isHomeLinkHrefEnabled = fg('csm_support_site_home_link_href');

	const handleHomeClick = useCallback(
		(event: React.MouseEvent | React.KeyboardEvent) => {
			if (
				event.defaultPrevented ||
				event.metaKey ||
				event.ctrlKey ||
				event.shiftKey ||
				event.altKey ||
				('button' in event && event.button !== 0)
			) {
				return;
			}

			// Keep router navigation relative; the href includes the hub base path.
			event.preventDefault();
			push(homePath);
		},
		[homePath, push],
	);

	const handleInboxClick = useCallback(() => {
		push(`/user/requests`);
	}, [push]);

	const handleProfileClick = useCallback(() => {
		setIsAvatarMenuOpen(!isAvatarMenuOpen);
	}, [isAvatarMenuOpen]);

	const handleAvatarMenuClose = useCallback(() => {
		setIsAvatarMenuOpen(false);
	}, []);

	const handleHamburgerClick = useCallback(() => {
		setIsMobileMenuOpen(true);
	}, []);

	const handleMobileMenuClose = useCallback(() => {
		setIsMobileMenuOpen(false);
	}, []);

	return (
		<React.Fragment>
			<Box
				ref={navbarRef}
				data-testId="navbar"
				{...(isEditMode && fg('hubs-disable-navbar-edit-focus') ? { inert: '' } : {})}
				xcss={cx(
					(isMenuStackingFixEnabled || isViewportSticky) && styles.navbarStacking,
					isNavBarUpdated ? styles.navbarUpdated : styles.navbar,
					isEditMode && styles.navBarDisabled,
				)}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic navigation styles from context
				style={isNavBarUpdated ? dynamicStyle : {}}
			>
				<Flex justifyContent="space-between" alignItems="center">
					<Box xcss={styles.logoContainer}>
						<Link
							to={homePath}
							{...(isHomeLinkHrefEnabled
								? {
										href: `${hubContext.env.relativeBaseUrl.replace(/\/$/, '')}${homePath}`,
										onClick: handleHomeClick,
									}
								: {})}
						>
							{logoUrl ? (
								<img
									height={getHubLogoHeight(hubType)}
									src={logoUrl}
									alt={formatMessage(messages.hubLogo)}
								/>
							) : (
								<IconButton
									appearance={isLoginButtonContrastEnabled ? 'subtle' : 'default'}
									icon={(iconProps) => <HomeIcon {...iconProps} color={iconColor} />}
									label={formatMessage(messages.hubLogo)}
									color={navTextColor}
								/>
							)}
						</Link>
					</Box>
					{!isAnonymousRoute && email && isNavBarUpdated && !isBelowSm && (
						<NavLinks textColor={navTextColor} backgroundColor={navStyle?.backgroundColor} />
					)}
					{!isAnonymousRoute && email && (
						<Box xcss={styles.rightSection}>
							{!isNavBarUpdated && (
								<React.Fragment>
									<div>{renderRightNavActions && renderRightNavActions()}</div>
									<IconButton
										appearance="subtle"
										icon={(iconProps) => <InboxIcon {...iconProps} color={iconColor} />}
										label="User requests"
										onClick={handleInboxClick}
									/>
								</React.Fragment>
							)}
							{shouldShowMobileNavLinks ? (
								isMobileMenuOpen ? (
									<IconButton
										appearance="subtle"
										icon={(iconProps) => <CrossIcon {...iconProps} color={iconColor} />}
										label={formatMessage(messages.closeMenu)}
										onClick={handleMobileMenuClose}
									/>
								) : (
									<IconButton
										appearance="subtle"
										icon={(iconProps) => <MenuIcon {...iconProps} color={iconColor} />}
										label={formatMessage(messages.openMenu)}
										onClick={handleHamburgerClick}
									/>
								)
							) : (
								<>
									{renderCustomNavActions && renderCustomNavActions(iconColor)}
									<Popup
										trigger={(triggerProps) => (
											<Profile
												{...triggerProps}
												tooltip="Profile and settings"
												icon={<Avatar appearance="circle" size="medium" src={avatarSrc} />}
												onClick={handleProfileClick}
											/>
										)}
										shouldRenderToParent
										placement="bottom-end"
										isOpen={isAvatarMenuOpen}
										content={() => <AvatarMenu />}
										onClose={handleAvatarMenuClose}
									/>
								</>
							)}
						</Box>
					)}
					{!isAnonymousRoute && !email && (
						<Link to={`/user/login`}>
							<Button appearance={isLoginButtonContrastEnabled ? 'subtle' : 'default'}>
								<Box
									as="span"
									// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic navigation text color from context
									style={isLoginButtonContrastEnabled ? { color: navTextColor } : undefined}
								>
									{formatMessage(messages.login)}
								</Box>
							</Button>
						</Link>
					)}
				</Flex>
			</Box>
			<Suspense fallback={null}>
				<ExitingPersistence appear>
					{isMobileMenuOpen && (
						<Motion
							enteringAnimationXcss={motionStyles.slideEntering}
							exitingAnimationXcss={motionStyles.slideExiting}
						>
							<MobileMenuOverlay onClose={handleMobileMenuClose} navbarRef={navbarRef} />
						</Motion>
					)}
				</ExitingPersistence>
			</Suspense>
		</React.Fragment>
	);
};
